import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpClient,
  HttpBackend,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, timeout } from 'rxjs/operators';
import { AppSettings } from '@core/config/app-settings';
import { RateLimitService } from '@core/services/rate-limit.service';

/**
 * Interceptor de sesión.
 *
 * Antes hacía una sola cosa ante un 401: borrar el storage y recargar a `/`.
 * Como el backend emite JWT con TTL de 60 minutos **absoluto desde el login**
 * (no desliza con la actividad) y nadie renovaba el token, eso expulsaba al
 * usuario a la hora en punto aunque estuviera trabajando.
 *
 * Ahora, ante un 401 se intenta renovar contra `POST /auth/refresh` y se
 * reintenta la petición original. Solo se cierra sesión si la renovación falla
 * (token fuera de `refresh_ttl`, revocado o manipulado).
 *
 * Dos detalles que importan:
 *
 *  - **Single-flight.** La app tiene varios pollers y streams simultáneos, así
 *    que al caducar el token llegan varios 401 a la vez. Sin coordinación, cada
 *    uno lanzaría su propio refresh y se invalidarían entre ellos. Aquí solo el
 *    primero llama a la API; el resto espera al mismo resultado.
 *  - **El refresh viaja por `HttpBackend`**, no por `HttpClient`, para saltarse
 *    esta misma cadena de interceptores. Si fuera por `HttpClient`, un 401 del
 *    propio refresh se interceptaría a sí mismo en bucle.
 */
@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private static readonly TOKEN_KEY = 'jwt_token';
  private static readonly REFRESH_PATH = '/auth/refresh';

  /**
   * Endpoints donde un 401 es una respuesta de negocio legítima (credenciales
   * incorrectas), no una sesión caducada. No deben limpiar storage ni recargar:
   * el formulario tiene que poder pintar el error.
   */
  private static readonly PUBLIC_AUTH_PATHS = [
    '/login',
    '/forgot-password',
    '/reset-password',
  ];

  /** Claves de sesión a limpiar al cerrar sesión. */
  private static readonly SESSION_KEYS = [
    'jwt_token',
    'user',
    'chat_id',
    'agent_id',
    'type_agent',
    'active_menu_section',
  ];

  urlsNotUse: Array<string>;

  /** Hay un refresh en vuelo ahora mismo. */
  private isRefreshing = false;

  /** Ya se está cerrando sesión por cuenta desactivada (llegan varios 403 a la vez). */
  private isHandlingInactiveAccount = false;

  /** Emite el token nuevo (o null mientras se resuelve) a las peticiones en cola. */
  private readonly refreshedToken$ = new BehaviorSubject<string | null>(null);

  /** Cliente sin interceptores, solo para la llamada de refresh. */
  private readonly rawHttp: HttpClient;

  constructor(
    private router: Router,
    private rateLimit: RateLimitService,
    httpBackend: HttpBackend,
  ) {
    this.urlsNotUse = [`/api/check/booking/seats`, `/users/register`];
    this.rawHttp = new HttpClient(httpBackend);
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (this.isValidRequestForInterceptor(request.url)) {
      const token = this.readToken();
      if (token) {
        request = this.withToken(request, token);
      }
    }

    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 429) {
          // NO es un problema de sesión: no cerramos nada. Solo marcamos el
          // cooldown para que los pollers se pausen en vez de realimentarlo.
          this.rateLimit.registerLimit(err.headers?.get('Retry-After'));
          console.warn(
            `[HTTP 429] Límite de peticiones alcanzado. Reintentar en ~${this.rateLimit.secondsRemaining()}s.`,
          );
          return throwError(() => err);
        }

        if (err.status === 403) {
          // EnsureAccountIsActive: la empresa o el usuario se desactivaron. Llega
          // en cualquier petición (chat, conexiones, alertas...), no solo en login.
          const code = this.readErrorCode(err);
          if (code === 'company_inactive' || code === 'user_inactive') {
            this.handleInactiveAccount(code);
          }
          return throwError(() => err);
        }

        if (err.status === 401) {
          // Credenciales incorrectas en login/recuperación: dejar pasar el error
          // para que el formulario lo muestre. Antes caía en la rama de abajo y
          // se recargaba la página, así que el usuario nunca veía el motivo.
          if (this.isPublicAuthEndpoint(request.url)) {
            return throwError(() => err);
          }

          // Un 401 del propio refresh significa que ya no hay nada que salvar.
          if (request.url.includes(JwtInterceptor.REFRESH_PATH)) {
            this.forceLogout();
            return throwError(() => err);
          }

          // Sin token guardado no hay nada que renovar (p. ej. login fallido).
          if (!this.readToken()) {
            this.forceLogout();
            return throwError(() => err);
          }

          return this.handleExpiredToken(request, next);
        }

        return throwError(() => err);
      }),
    );
  }

  /**
   * Renueva el token y reintenta la petición. Si ya hay un refresh en vuelo,
   * espera a que termine en vez de lanzar otro.
   */
  private handleExpiredToken(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (this.isRefreshing) {
      return this.refreshedToken$.pipe(
        filter((token): token is string => token !== null),
        take(1),
        switchMap((token) => next.handle(this.withToken(request, token))),
      );
    }

    this.isRefreshing = true;
    this.refreshedToken$.next(null);

    return this.rawHttp
      .post<{ access_token: string }>(
        `${AppSettings.API_URI}${JwtInterceptor.REFRESH_PATH}`,
        {},
        { headers: { Authorization: `Bearer ${this.readToken()}` } },
      )
      .pipe(
        switchMap((response) => {
          const token = response?.access_token;
          if (!token) {
            throw new Error('refresh sin access_token');
          }

          this.storeToken(token);
          this.isRefreshing = false;
          this.refreshedToken$.next(token);

          return next.handle(this.withToken(request, token));
        }),
        catchError((refreshErr) => {
          this.isRefreshing = false;
          this.forceLogout();
          return throwError(() => refreshErr);
        }),
      );
  }

  /**
   * ¿La URL es uno de los endpoints públicos de autenticación?
   *
   * Se compara contra la base de la API y se exige que el segmento termine ahí
   * (fin de cadena, `/` o `?`). Sin ese corte, `/login` casaría también con
   * `/login-logs` y `/login-stats`, que sí son rutas autenticadas y deben seguir
   * intentando renovar el token.
   */
  private isPublicAuthEndpoint(url: string): boolean {
    return JwtInterceptor.PUBLIC_AUTH_PATHS.some((path) => {
      const base = `${AppSettings.API_URI}${path}`;
      if (!url.startsWith(base)) {
        return false;
      }
      const next = url.charAt(base.length);
      return next === '' || next === '/' || next === '?';
    });
  }

  private withToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  private readToken(): string | null {
    return (
      localStorage.getItem(JwtInterceptor.TOKEN_KEY) ??
      sessionStorage.getItem(JwtInterceptor.TOKEN_KEY)
    );
  }

  /**
   * Guarda el token nuevo en el MISMO storage donde estaba el anterior, para
   * respetar la elección de "recordarme" del usuario.
   */
  private storeToken(token: string): void {
    if (localStorage.getItem(JwtInterceptor.TOKEN_KEY) !== null) {
      localStorage.setItem(JwtInterceptor.TOKEN_KEY, token);
    } else {
      sessionStorage.setItem(JwtInterceptor.TOKEN_KEY, token);
    }
  }

  /**
   * Cierra sesión de verdad. Recarga completa a propósito: `router.navigate` no
   * destruye los BehaviorSubject de los servicios singleton y quedarían datos
   * del usuario anterior en memoria.
   */
  private forceLogout(): void {
    console.error('Sesión no recuperable. Limpiando y redirigiendo a login...');
    this.clearSession();
    window.location.href = '/';
  }

  /**
   * Cierra sesión y lleva a la pantalla explicativa. `auth/logout` sigue
   * abierto a propósito para cuentas desactivadas: se llama para invalidar el
   * token en servidor, con timeout para no dejar al usuario esperando.
   */
  private handleInactiveAccount(code: 'company_inactive' | 'user_inactive'): void {
    if (this.isHandlingInactiveAccount) {
      return;
    }
    this.isHandlingInactiveAccount = true;

    const params = new URLSearchParams({ reason: code === 'user_inactive' ? 'user' : 'company' });
    const companyName = this.readStoredCompanyName();
    if (companyName) {
      params.set('company', companyName);
    }

    const redirect = () => {
      this.clearSession();
      window.location.href = `/account-inactive?${params.toString()}`;
    };

    const token = this.readToken();
    if (!token) {
      redirect();
      return;
    }

    this.rawHttp
      .get(`${AppSettings.API_URI}/auth/logout`, { headers: { Authorization: `Bearer ${token}` } })
      .pipe(timeout(2500))
      .subscribe({ next: redirect, error: redirect });
  }

  private readErrorCode(err: HttpErrorResponse): string {
    const body = err?.error;
    return String(body?.error ?? body?.code ?? '').trim().toLowerCase();
  }

  private readStoredCompanyName(): string {
    try {
      const raw = localStorage.getItem('user') ?? sessionStorage.getItem('user');
      const user = raw ? JSON.parse(raw) : null;
      return String(user?.company?.name ?? user?.company_name ?? '').trim();
    } catch {
      return '';
    }
  }

  private clearSession(): void {
    JwtInterceptor.SESSION_KEYS.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    sessionStorage.removeItem('login_destination');
  }

  private isValidRequestForInterceptor(requestUrl: string): boolean {
    let positionIndicator: string = '/';
    let position = requestUrl.indexOf(positionIndicator);
    if (position > 0) {
      let destination: string = requestUrl.substring(
        position + positionIndicator.length,
      );
      for (let address of this.urlsNotUse) {
        if (new RegExp(address).test(destination)) {
          return false;
        }
      }
    }
    return true;
  }
}
